import {
  ACCESS_TIERS,
  CONTENT_OWNER_TYPES,
  PLATFORM_COLLECTIONS,
  PLATFORM_ROLES,
} from './platformSchema';
import { resolvePlatformRole } from './organizationContext';

export function normalizeContentPackRecord(record = {}, fallbackId = '') {
  const id = record.id || record.contentPackId || fallbackId || '';

  return {
    id,
    contentPackId: id,
    name: record.name || record.title || id,
    description: record.description || '',
    ownerType: record.ownerType || CONTENT_OWNER_TYPES.PLATFORM,
    ownerId: record.ownerId || 'platform',
    industry: record.industry || '',
    disciplines: Array.isArray(record.disciplines) ? record.disciplines.filter(Boolean) : [],
    isPremium: Boolean(record.isPremium),
    status: record.status || 'active',
    createdAt: record.createdAt || null,
  };
}

export function normalizeEntitlementRecord(record = {}, fallbackId = '') {
  const id = record.id || record.entitlementId || fallbackId || '';

  return {
    id,
    entitlementId: id,
    organizationId: record.organizationId || '',
    contentPackId: record.contentPackId || '',
    status: record.status || 'active',
    startDate: record.startDate || record.startsAt || null,
    endDate: record.endDate || record.endsAt || null,
    sourceType: record.sourceType || 'manual_grant',
    sourceId: record.sourceId || '',
    billingStatusSnapshot: record.billingStatusSnapshot || '',
    grantedByUserId: record.grantedByUserId || '',
    grantedReason: record.grantedReason || '',
  };
}

export function organizationHasEntitlement(
  organizationId = '',
  contentPackId = '',
  entitlements = []
) {
  if (!organizationId || !contentPackId) return false;

  const now = Date.now();

  return (entitlements || [])
    .map((entitlement) => normalizeEntitlementRecord(entitlement))
    .some((entitlement) => {
      if (
        entitlement.organizationId !== organizationId ||
        entitlement.contentPackId !== contentPackId ||
        entitlement.status !== 'active'
      ) {
        return false;
      }

      const endTime = entitlement.endDate?.seconds
        ? entitlement.endDate.seconds * 1000
        : entitlement.endDate
          ? new Date(entitlement.endDate).getTime()
          : null;

      return !endTime || endTime >= now;
    });
}

export function canAccessContentPack(
  contentPack = {},
  {
    userData = {},
    organizationIds = [],
    entitlements = [],
  } = {}
) {
  const normalizedPack = normalizeContentPackRecord(contentPack);
  const platformRole = resolvePlatformRole(userData);

  if (
    platformRole === PLATFORM_ROLES.PLATFORM_SUPER_ADMIN ||
    platformRole === PLATFORM_ROLES.PLATFORM_CONTENT_ADMIN
  ) {
    return true;
  }

  if (!normalizedPack.isPremium) {
    return true;
  }

  if (
    normalizedPack.ownerType === CONTENT_OWNER_TYPES.ORGANIZATION &&
    organizationIds.includes(normalizedPack.ownerId)
  ) {
    return true;
  }

  return organizationIds.some((organizationId) =>
    organizationHasEntitlement(organizationId, normalizedPack.contentPackId, entitlements)
  );
}

export function getAccessibleOrganizationContentPacks(
  organizationId = '',
  contentPacks = [],
  entitlements = []
) {
  return (contentPacks || [])
    .map((contentPack) => normalizeContentPackRecord(contentPack))
    .filter((contentPack) => {
      if (!contentPack.isPremium) return true;
      if (contentPack.ownerType === CONTENT_OWNER_TYPES.ORGANIZATION && contentPack.ownerId === organizationId) {
        return true;
      }
      return organizationHasEntitlement(organizationId, contentPack.contentPackId, entitlements);
    });
}

export function getUnavailablePremiumContentPacks(
  organizationId = '',
  contentPacks = [],
  entitlements = []
) {
  return (contentPacks || [])
    .map((contentPack) => normalizeContentPackRecord(contentPack))
    .filter((contentPack) => contentPack.isPremium)
    .filter((contentPack) => !getAccessibleOrganizationContentPacks(organizationId, [contentPack], entitlements).length);
}

export function getOrganizationEntitlementSummary(
  organizationId = '',
  {
    contentPacks = [],
    entitlements = [],
    templates = [],
  } = {}
) {
  const accessiblePacks = getAccessibleOrganizationContentPacks(
    organizationId,
    contentPacks,
    entitlements
  );
  const unavailablePremiumPacks = getUnavailablePremiumContentPacks(
    organizationId,
    contentPacks,
    entitlements
  );
  const premiumTemplatesInUse = (templates || []).filter(
    (template) => template.accessTier === ACCESS_TIERS.PREMIUM || Boolean(template.contentPackId)
  );

  return {
    accessiblePacks,
    unavailablePremiumPacks,
    premiumTemplatesInUse,
  };
}

export function attachContentPackToDrillTemplate(template = {}, contentPack = {}) {
  const normalizedPack = normalizeContentPackRecord(contentPack);

  return {
    ...template,
    contentPackId: normalizedPack.contentPackId,
    accessTier: normalizedPack.isPremium ? ACCESS_TIERS.PREMIUM : (template.accessTier || ACCESS_TIERS.FREE),
  };
}

export const CONTENT_PACK_COLLECTION = PLATFORM_COLLECTIONS.CONTENT_PACKS;
export const ENTITLEMENT_COLLECTION = PLATFORM_COLLECTIONS.ENTITLEMENTS;
